import { useEffect } from "react";

import { Workspace } from "@/types/generated/management-api-types";
import Skeleton from "@/common/Skeleton/Skeleton.tsx";
import { useGetWorkspacesQuery } from "@/services/apis/api.ts";
import { Avatar } from "@/catalyst/avatar.tsx";
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from "@/catalyst/dropdown.tsx";
import { SidebarItem, SidebarLabel } from "@/catalyst/sidebar.tsx";
import { ChevronDownIcon, PlusIcon, StarIcon } from "@heroicons/react/16/solid";
import WithFeatureFlagCheck from "@/common/WithFeatureFlagCheck";
import { useFeatureFlagManager } from "@/hooks/use-feature-flag-manager.ts";
import { useOptionalSelectedWorkspace } from "@/hooks/use-selected-workspace.ts";
import { useUser } from "@/hooks/use-user";
import ManagerPicker from "./ManagerPicker.tsx";
import {
    PickerTriggerContent,
    useSwitchWorkspace,
    WorkspaceAvatar,
} from "./parts.tsx";

export default function WorkspacePicker({
    setNewWorkspaceDialogOpen,
    className,
}: {
    setNewWorkspaceDialogOpen: SetState<
        { managedOrganizationId?: string } | false
    >;
    className?: string;
}) {
    const { data, isLoading } = useGetWorkspacesQuery(undefined);

    const selectedWorkspace = useOptionalSelectedWorkspace();

    const selected = data?.find((d) => d.id === selectedWorkspace?.id);

    const hasNoWorkspacesYet = data?.length === 0 && !isLoading;

    // The workspace slice falls back to the first workspace when the selection
    // is not in the list, so this only has to prompt for a first one.
    useEffect(() => {
        if (hasNoWorkspacesYet) {
            setNewWorkspaceDialogOpen({});
        }
    }, [hasNoWorkspacesYet, setNewWorkspaceDialogOpen]);

    return typeof data === "undefined" ||
        typeof selected === "undefined" ||
        isLoading ? (
        <Dropdown>
            <DropdownButton className={className} as={SidebarItem}>
                <Avatar initials={"aa"} className="opacity-0" />
                <SidebarLabel>
                    <Skeleton />
                </SidebarLabel>
                <ChevronDownIcon />
            </DropdownButton>
        </Dropdown>
    ) : (
        <LoadedPicker
            selected={selected}
            workspaces={data}
            setNewWorkspaceDialogOpen={setNewWorkspaceDialogOpen}
        />
    );
}

type PickerProps = {
    selected: Workspace;
    workspaces: Workspace[];
    setNewWorkspaceDialogOpen: SetState<
        { managedOrganizationId?: string } | false
    >;
};

function LoadedPicker(props: PickerProps) {
    const user = useUser();

    return user.isManager() ? (
        <ManagerPicker {...props} user={user} />
    ) : (
        <PickerWorkspace {...props} />
    );
}

function PickerWorkspace({
    selected,
    workspaces,
    setNewWorkspaceDialogOpen,
}: PickerProps) {
    const switchWorkspace = useSwitchWorkspace();
    const featureFlagManager = useFeatureFlagManager();

    return (
        <Dropdown>
            <DropdownButton as={SidebarItem}>
                <PickerTriggerContent selected={selected} />
            </DropdownButton>
            <DropdownMenu
                className="min-w-80 lg:min-w-64"
                anchor="bottom start"
            >
                {workspaces.map((workspace) => (
                    <DropdownItem
                        key={workspace.id}
                        onClick={() => switchWorkspace(workspace)}
                    >
                        <WorkspaceAvatar slot="icon" workspace={workspace} />
                        <DropdownLabel>{workspace.name}</DropdownLabel>
                    </DropdownItem>
                ))}
                <DropdownDivider />
                <WithFeatureFlagCheck
                    passesCheck={featureFlagManager.isWithinThreshold(
                        "workspace-limit",
                    )}
                    opener={"action-button-workspace-limit"}
                >
                    {(needsToUpgrade) => (
                        <DropdownItem
                            onClick={() => setNewWorkspaceDialogOpen({})}
                        >
                            {needsToUpgrade ? <StarIcon /> : <PlusIcon />}
                            <DropdownLabel>New workspace&hellip;</DropdownLabel>
                        </DropdownItem>
                    )}
                </WithFeatureFlagCheck>
            </DropdownMenu>
        </Dropdown>
    );
}
